import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  CreditCard, 
  DollarSign,
  ArrowRight,
  MessageSquare,
  PieChart
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const StatCard = ({ title, value, icon: Icon, trend, trendLabel, color = "primary", accentColor }) => (
  <Card className="bg-card border-border card-hover" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-2xl font-heading font-bold text-foreground">{value}</p>
          {trend !== undefined && (
            <p className={`text-xs mt-2 flex items-center gap-1 ${trend >= 0 ? 'text-emerald-400' : 'text-[#ff9787]'}`}>
              {trend >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {Math.abs(trend)}% {trendLabel}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color === 'green' ? 'bg-emerald-500/10' : (accentColor ? 'bg-[#ff9787]/10' : (color === 'primary' ? 'bg-primary/10' : 'bg-accent'))}`}>
          <Icon size={24} className={color === 'green' ? 'text-emerald-400' : (accentColor ? 'text-[#ff9787]' : (color === 'primary' ? 'text-primary' : 'text-foreground'))} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const COLORS = ['hsl(190, 100%, 50%)', 'hsl(12, 100%, 77%)', 'hsl(160, 60%, 45%)', 'hsl(280, 65%, 60%)', 'hsl(340, 75%, 55%)'];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass rounded-lg px-3 py-2">
        <p className="text-sm text-foreground">{formatCurrency(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/reports/dashboard`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mock chart data based on real data
  const chartData = data?.recent_transactions?.map((t, i) => ({
    name: t.date,
    value: Math.abs(t.lines?.reduce((sum, l) => sum + l.debit, 0) || 0)
  })).reverse() || [];

  const pieData = [
    { name: 'Attività', value: Math.abs(data?.summary?.total_assets || 0) },
    { name: 'Passività', value: Math.abs(data?.summary?.total_liabilities || 0) },
    { name: 'Ricavi', value: Math.abs(data?.summary?.total_revenues || 0) },
    { name: 'Costi', value: Math.abs(data?.summary?.total_expenses || 0) },
  ].filter(d => d.value > 0);

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Panoramica del budget aziendale</p>
        </div>
        <Button 
          onClick={() => navigate('/chat')}
          className="bg-primary text-primary-foreground hover:bg-primary/90 btn-primary-glow"
          data-testid="go-to-chat-btn"
        >
          <MessageSquare size={18} className="mr-2" />
          Chiedi all'AI
          <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Totale Attività" 
          value={formatCurrency(data?.summary?.total_assets || 0)}
          icon={Wallet}
          color="green"
        />
        <StatCard 
          title="Totale Passività" 
          value={formatCurrency(data?.summary?.total_liabilities || 0)}
          icon={CreditCard}
          accentColor
        />
        <StatCard 
          title="Ricavi" 
          value={formatCurrency(data?.summary?.total_revenues || 0)}
          icon={TrendingUp}
          color="primary"
        />
        <StatCard 
          title="Utile Netto" 
          value={formatCurrency(data?.summary?.net_income || 0)}
          icon={DollarSign}
          color="primary"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Area Chart */}
        <Card className="bg-card border-border" data-testid="transactions-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Andamento Movimenti</CardTitle>
            <CardDescription>Ultimi movimenti registrati</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(190, 100%, 50%)" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(190, 100%, 50%)" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(217, 33%, 17%)" />
                    <XAxis dataKey="name" stroke="hsl(215, 20%, 55%)" fontSize={12} />
                    <YAxis stroke="hsl(215, 20%, 55%)" fontSize={12} tickFormatter={(v) => `€${v}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="hsl(190, 100%, 50%)" 
                      fillOpacity={1} 
                      fill="url(#colorValue)" 
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <PieChart size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Nessun dato disponibile</p>
                    <p className="text-sm">Registra i primi movimenti</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card className="bg-card border-border" data-testid="distribution-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Distribuzione</CardTitle>
            <CardDescription>Composizione del budget</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </RechartsPie>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <PieChart size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Nessun dato disponibile</p>
                  </div>
                </div>
              )}
            </div>
            {pieData.length > 0 && (
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {pieData.map((entry, index) => (
                  <div key={entry.name} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-sm text-muted-foreground">{entry.name}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card className="bg-card border-border" data-testid="recent-transactions">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading text-lg">Ultimi Movimenti</CardTitle>
            <CardDescription>Le tue transazioni recenti</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/transactions')}
            data-testid="view-all-transactions-btn"
          >
            Vedi tutti
            <ArrowRight size={14} className="ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {data?.recent_transactions?.length > 0 ? (
            <div className="space-y-3">
              {data.recent_transactions.slice(0, 5).map((t, i) => (
                <div 
                  key={t.id} 
                  className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border"
                >
                  <div>
                    <p className="font-medium text-foreground">{t.description}</p>
                    <p className="text-sm text-muted-foreground">{t.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-foreground">
                      {formatCurrency(t.lines?.reduce((sum, l) => sum + l.debit, 0) || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">{t.created_by}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nessun movimento registrato</p>
              <Button 
                variant="link" 
                className="text-primary mt-2"
                onClick={() => navigate('/transactions')}
              >
                Registra il primo movimento
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-card border border-border text-center">
          <p className="text-3xl font-heading font-bold text-primary">{data?.account_count || 0}</p>
          <p className="text-sm text-muted-foreground">Conti</p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border text-center">
          <p className="text-3xl font-heading font-bold text-foreground">{data?.transaction_count || 0}</p>
          <p className="text-sm text-muted-foreground">Movimenti</p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border text-center">
          <p className="text-3xl font-heading font-bold text-foreground">{formatCurrency(data?.summary?.equity || 0)}</p>
          <p className="text-sm text-muted-foreground">Patrimonio</p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border text-center">
          <p className={`text-3xl font-heading font-bold ${(data?.summary?.net_income || 0) >= 0 ? 'text-emerald-400' : 'text-[#ff9787]'}`}>
            {(data?.summary?.net_income || 0) >= 0 ? '+' : ''}{formatCurrency(data?.summary?.net_income || 0)}
          </p>
          <p className="text-sm text-muted-foreground">Risultato</p>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
